import React, { useMemo } from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import clsx from "clsx";
import { AgendaGroup, AgendaJobData } from "../agenda.types";
import {
	getJobColorClass,
	formatJobDateRange,
	getFontSizeForRowHeight,
	HOUR_COL_WIDTH,
	DAY_COL_WIDTH,
	WEEK_COL_WIDTH,
	WeekRange,
} from "./utils";
import { ChevronDown, ChevronRight } from "lucide-react";
import { JobBar } from "./job-timeline.component";

interface ClientSectionProps {
	group: AgendaGroup;
	expanded: boolean;
	onToggle: (groupId: string) => void;
	even: boolean;
	daysInRange: Date[];
	viewMode: "day" | "week" | "hour";
	weeksInRange: WeekRange[];
	hoursInRange: number[];
	timelineWidth: number;
	sidebarWidth: number;
	hourViewDate?: string;
	onJobNavigate: (groupId: string, jobId: number) => void;
	onJobClick: (job: AgendaJobData, groupId: string) => void;
	colWidth?: number;
	rowHeight?: number;
	profileColors?: Record<string, string>;
	profilesMap?: Record<string, { first_name?: string; last_name?: string }>;
}

function getSummaryBars(
	jobs: AgendaJobData[],
	daysInRange: Date[],
	viewMode: "day" | "week" | "hour",
	weeksInRange: WeekRange[],
	colWidth?: number
): { left: number; width: number }[] {
	if (viewMode === "hour" || jobs.length === 0) return [];

	const msPerDay = 86400000;
	const bars: { left: number; width: number }[] = [];

	for (const j of jobs) {
		if (!j.incoming_order_date || !j.outgoing_order_date) continue;
		const jobStart = new Date(
			new Date(j.incoming_order_date).toDateString()
		);
		const jobEnd = new Date(new Date(j.outgoing_order_date).toDateString());

		if (viewMode === "day") {
			if (daysInRange.length === 0) continue;
			const dayW = colWidth ?? DAY_COL_WIDTH;
			const rangeStart = new Date(daysInRange[0].toDateString());
			const rangeEnd = new Date(
				daysInRange[daysInRange.length - 1].toDateString()
			);
			if (jobEnd < rangeStart || jobStart > rangeEnd) continue;
			const clampedStart = jobStart < rangeStart ? rangeStart : jobStart;
			const clampedEnd = jobEnd > rangeEnd ? rangeEnd : jobEnd;
			const startIdx =
				(clampedStart.getTime() - rangeStart.getTime()) / msPerDay;
			const endIdx =
				(clampedEnd.getTime() - rangeStart.getTime()) / msPerDay;
			bars.push({
				left: startIdx * dayW,
				width: (endIdx - startIdx + 1) * dayW,
			});
		} else {
			if (weeksInRange.length === 0) continue;
			const weekW = colWidth ?? WEEK_COL_WIDTH;
			const rangeStart = weeksInRange[0].start;
			const rangeEnd = weeksInRange[weeksInRange.length - 1].end;
			if (jobEnd < rangeStart || jobStart > rangeEnd) continue;
			const clampedStart = jobStart < rangeStart ? rangeStart : jobStart;
			const clampedEnd = jobEnd > rangeEnd ? rangeEnd : jobEnd;
			const pixelsPerDay = weekW / 7;
			const startDayOffset =
				(clampedStart.getTime() - rangeStart.getTime()) / msPerDay;
			const endDayOffset =
				(clampedEnd.getTime() - rangeStart.getTime()) / msPerDay;
			bars.push({
				left: startDayOffset * pixelsPerDay,
				width: (endDayOffset - startDayOffset + 1) * pixelsPerDay,
			});
		}
	}

	return bars;
}

export const ClientSection: React.FC<ClientSectionProps> = React.memo(
	({
		group,
		expanded,
		onToggle,
		even,
		daysInRange,
		viewMode,
		weeksInRange,
		hoursInRange,
		timelineWidth,
		sidebarWidth,
		hourViewDate,
		onJobNavigate,
		onJobClick,
		colWidth,
		rowHeight = 20,
		profileColors,
		profilesMap,
	}) => {
		const stickyBg = even ? "bg-gray-50" : "bg-white";
		const rh = rowHeight;
		const barH = Math.max(8, rh - 6);
		const barTop = Math.floor((rh - barH) / 2);
		const fs = getFontSizeForRowHeight(rh);

		const summaryBars = useMemo(
			() =>
				getSummaryBars(
					group.jobs,
					daysInRange,
					viewMode,
					weeksInRange,
					colWidth
				),
			[group.jobs, daysInRange, viewMode, weeksInRange, colWidth]
		);

		return (
			<>
				<div
					className={clsx("flex", even && "bg-gray-50/80")}
					style={{ height: rh }}
				>
					<div
						onClick={() => onToggle(group.id)}
						className={clsx(
							"flex items-center gap-1.5 pl-1.5 cursor-pointer hover:bg-gray-100 transition-colors duration-150",
							"sticky left-0 z-10 border-r border-gray-200 shrink-0",
							stickyBg
						)}
						style={{
							width: sidebarWidth,
							minWidth: sidebarWidth,
						}}
						data-testid={`agenda-client-row-${group.id}`}
						aria-label={`Client ${group.name}`}
					>
						<div className="flex items-center justify-center w-4 h-4 text-gray-400">
							{expanded ? (
								<ChevronDown className="h-3 w-3" />
							) : (
								<ChevronRight className="h-3 w-3" />
							)}
						</div>
						<span
							className="font-semibold text-gray-700 truncate"
							style={{ fontSize: fs }}
						>
							{group.name}
						</span>
						<span
							className="tabular-nums text-gray-400 shrink-0"
							style={{ fontSize: fs - 1 }}
						>
							{group.jobs.length}
						</span>
					</div>
					<div
						onClick={() => onToggle(group.id)}
						style={{
							width: timelineWidth,
							...(viewMode === "hour"
								? {
										backgroundImage: `repeating-linear-gradient(to right, #e5e7eb 0px, #e5e7eb 1px, transparent 1px, transparent ${
											colWidth ?? HOUR_COL_WIDTH
										}px)`,
										backgroundSize: `${
											colWidth ?? HOUR_COL_WIDTH
										}px 100%`,
									}
								: {}),
						}}
						className="relative shrink-0 cursor-pointer"
					>
						{summaryBars.map((bar, i) => (
							<div
								key={i}
								className="absolute rounded-sm bg-gray-300/50"
								style={{
									left: bar.left,
									width: bar.width,
									top: barTop,
									height: barH,
								}}
							/>
						))}
					</div>
				</div>

				{expanded && (
					<TooltipPrimitive.Provider delayDuration={300}>
						{group.jobs.map((job) => (
							<div
								key={job.id}
								className={clsx(
									"flex",
									even && "bg-gray-50/80"
								)}
								style={{ height: rh }}
							>
								<TooltipPrimitive.Root>
									<TooltipPrimitive.Trigger asChild>
										<div
											onClick={() =>
												onJobNavigate(group.id, job.id)
											}
											className={clsx(
												"flex items-center gap-2 pl-8 text-xs cursor-pointer hover:bg-gray-100 transition-colors duration-150",
												"sticky left-0 z-10 border-r border-gray-200 shrink-0",
												stickyBg
											)}
											style={{
												width: sidebarWidth,
												minWidth: sidebarWidth,
											}}
											data-testid={`agenda-job-sidebar-${job.id}`}
											aria-label={`Job ${job.code}`}
										>
											<div
												className={clsx(
													"w-2.5 h-2.5 rounded-sm shrink-0",
													getJobColorClass(
														job.status,
														job.is_untouched
													) ?? ""
												)}
											/>
											<div
												className="font-mono text-gray-500 shrink-0"
												style={{ fontSize: fs }}
											>
												{job.code}
											</div>
											<div
												className="font-medium text-gray-700 truncate"
												style={{ fontSize: fs }}
											>
												{job.title}
											</div>
											{(job.patient_first_name ||
												job.patient_last_name) && (
												<>
													<span
														className="text-gray-300 shrink-0"
														style={{ fontSize: fs }}
													>
														·
													</span>
													<div
														className="text-gray-500 truncate"
														style={{ fontSize: fs }}
													>
														{[
															job.patient_first_name,
															job.patient_last_name,
														]
															.filter(Boolean)
															.join(" ")}
													</div>
												</>
											)}
										</div>
									</TooltipPrimitive.Trigger>
									<TooltipPrimitive.Content
										side="right"
										className="z-50 overflow-hidden rounded-md border bg-popover px-3 py-1.5 text-sm text-popover-foreground shadow-md animate-in fade-in-0 zoom-in-95"
										sideOffset={4}
									>
										<div>{job.title}</div>
										{(job.patient_first_name ||
											job.patient_last_name) && (
											<div className="text-xs text-muted-foreground">
												{[
													job.patient_first_name,
													job.patient_last_name,
												]
													.filter(Boolean)
													.join(" ")}
											</div>
										)}
										<div className="text-xs text-muted-foreground">
											{formatJobDateRange(job)}
										</div>
									</TooltipPrimitive.Content>
								</TooltipPrimitive.Root>
								<div
									style={{
										width: timelineWidth,
										...(viewMode === "hour"
											? {
													backgroundImage: `repeating-linear-gradient(to right, #e5e7eb 0px, #e5e7eb 1px, transparent 1px, transparent ${
														colWidth ??
														HOUR_COL_WIDTH
													}px)`,
													backgroundSize: `${
														colWidth ??
														HOUR_COL_WIDTH
													}px 100%`,
												}
											: {}),
									}}
									className="relative shrink-0"
								>
									<JobBar
										job={job}
										daysInRange={daysInRange}
										viewMode={viewMode}
										weeksInRange={weeksInRange}
										hoursInRange={hoursInRange}
										hourViewDate={hourViewDate}
										colWidth={colWidth}
										rowHeight={rh}
										onJobClick={(j) =>
											onJobClick(j, group.id)
										}
										profileColors={profileColors}
										profilesMap={profilesMap}
									/>
								</div>
							</div>
						))}
					</TooltipPrimitive.Provider>
				)}
			</>
		);
	}
);

interface FlatJobRowProps {
	job: AgendaJobData;
	groupId: string;
	groupName: string;
	even: boolean;
	daysInRange: Date[];
	viewMode: "day" | "week" | "hour";
	weeksInRange: WeekRange[];
	hoursInRange: number[];
	timelineWidth: number;
	sidebarWidth: number;
	hourViewDate?: string;
	onJobNavigate: (groupId: string, jobId: number) => void;
	onJobClick: (job: AgendaJobData, groupId: string) => void;
	colWidth?: number;
	rowHeight?: number;
	profileColors?: Record<string, string>;
	profilesMap?: Record<string, { first_name?: string; last_name?: string }>;
}

export const FlatJobRow: React.FC<FlatJobRowProps> = React.memo(
	({
		job,
		groupId,
		groupName,
		even,
		daysInRange,
		viewMode,
		weeksInRange,
		hoursInRange,
		timelineWidth,
		sidebarWidth,
		hourViewDate,
		onJobNavigate,
		onJobClick,
		colWidth,
		rowHeight = 20,
		profileColors,
		profilesMap,
	}) => {
		const stickyBg = even ? "bg-gray-50" : "bg-white";
		const clientName = job.client_name ?? groupName;
		const rh = rowHeight;
		const barH = Math.max(8, rh - 6);
		const fs = getFontSizeForRowHeight(rh);

		return (
			<div
				className={clsx("flex", even && "bg-gray-50/80")}
				style={{ height: rh }}
			>
				<TooltipPrimitive.Provider delayDuration={300}>
					<TooltipPrimitive.Root>
						<TooltipPrimitive.Trigger asChild>
							<div
								onClick={() =>
									onJobNavigate(
										job.client_id ?? groupId,
										job.id
									)
								}
								className={clsx(
									"flex items-center gap-1.5 pl-2 text-xs cursor-pointer hover:bg-gray-100 transition-colors duration-150",
									"sticky left-0 z-10 border-r border-gray-200 shrink-0",
									stickyBg
								)}
								style={{
									width: sidebarWidth,
									minWidth: sidebarWidth,
								}}
								data-testid={`agenda-flat-row-${job.id}`}
								aria-label={`Job ${job.code}`}
							>
								<div
									className={clsx(
										"w-2.5 h-2.5 rounded-sm shrink-0",
										getJobColorClass(
											job.status,
											job.is_untouched
										) ?? ""
									)}
								/>
								<span
									className="text-gray-500 truncate"
									style={{ fontSize: fs }}
								>
									{clientName}
								</span>
								<span
									className="text-black-800 shrink-0"
									style={{ fontSize: fs }}
								>
									·
								</span>
								<span
									className="font-mono text-gray-600 shrink-0"
									style={{ fontSize: fs }}
								>
									{job.code}
								</span>
								{barH < 12 && (
									<>
										{job.title && (
											<span
												className="text-gray-500 truncate"
												style={{ fontSize: fs }}
											>
												{job.title}
											</span>
										)}
									</>
								)}
								{(job.patient_first_name ||
									job.patient_last_name) && (
									<>
										<span
											className="text-black-800 shrink-0"
											style={{ fontSize: fs }}
										>
											·
										</span>
										<span
											className="text-black-800 truncate"
											style={{ fontSize: fs }}
										>
											{[
												job.patient_first_name,
												job.patient_last_name,
											]
												.filter(Boolean)
												.join(" ")}
										</span>
									</>
								)}
							</div>
						</TooltipPrimitive.Trigger>
						<TooltipPrimitive.Content
							side="right"
							className="z-50 overflow-hidden rounded-md border bg-popover px-3 py-1.5 text-sm text-popover-foreground shadow-md animate-in fade-in-0 zoom-in-95"
							sideOffset={4}
						>
							<div className="font-medium">
								{clientName} · {job.code}
							</div>
							<div>{job.title}</div>
							{(job.patient_first_name ||
								job.patient_last_name) && (
								<div className="text-xs text-muted-foreground">
									{[
										job.patient_first_name,
										job.patient_last_name,
									]
										.filter(Boolean)
										.join(" ")}
								</div>
							)}
							<div className="text-xs text-muted-foreground">
								{formatJobDateRange(job)}
							</div>
						</TooltipPrimitive.Content>
					</TooltipPrimitive.Root>
				</TooltipPrimitive.Provider>
				<div
					style={{
						width: timelineWidth,
						...(viewMode === "hour"
							? {
									backgroundImage: `repeating-linear-gradient(to right, #e5e7eb 0px, #e5e7eb 1px, transparent 1px, transparent ${
										colWidth ?? HOUR_COL_WIDTH
									}px)`,
									backgroundSize: `${
										colWidth ?? HOUR_COL_WIDTH
									}px 100%`,
								}
							: {}),
					}}
					className="relative shrink-0"
				>
					<JobBar
						job={job}
						daysInRange={daysInRange}
						viewMode={viewMode}
						weeksInRange={weeksInRange}
						hoursInRange={hoursInRange}
						hourViewDate={hourViewDate}
						colWidth={colWidth}
						rowHeight={rh}
						onJobClick={(j) =>
							onJobClick(j, j.client_id ?? groupId)
						}
						profileColors={profileColors}
						profilesMap={profilesMap}
					/>
				</div>
			</div>
		);
	}
);
